<template>
  <div
    class="relative"
  >
    <div class="relative">
      <TagBase size="base" class="text-shadow-none -ml-0.5 mb-4" :style="{ backgroundColor: `rgb(${palette.base.color.join(',')})`, color: `rgb(${palette.base.bg.join(',')})`, boxShadow: `0 2px 10px rgba(0, 0, 0, 0.25)` }" v-if="!event.isPastAfter && isTitle">
        {{ event.date ? dayjs(event.date).fromNow() : $t('moment.empty') }}
      </TagBase>

      <p class="font-bold text-3xl mb-3 !leading-[1.12]" v-if="isTitle">{{ event.translated(currentUser, langExcluded).title }}</p>

      <div class="bg-ruby-200 text-shadow-none text-ruby-950 space-x-2 flex items-center py-3.5 px-4 rounded-md font-medium leading-tight mb-6" v-if="event.attendees.find(a => currentUser?.blocked.includes(a._id) && a.type === 'attending')">
        <ImageEmoji name="Prohibited" class="size-8" size="sm" />
        <p class="grow">{{ $t('components.Event.page.blocked') }}</p>
      </div>

      <TextHider2
        class="grow leading-normal font-medium"
        :gradient-style="{
          '--tw-gradient-from': `rgba(${palette.base.bg.join(',')}, 0)`,
          '--tw-gradient-to': `rgba(${palette.base.bg.join(',')}, 1)`
        }"
        :bg-style="{
          background: `rgba(${palette.base.bg.join(',')}, 1)`
        }"
        :lines="5"
        v-if="event.description && event.description.length > 8"
      >
        <div class="text-body leading-relaxed font-medium" style="color: inherit;" v-html="nl2br(event.translated(currentUser, langExcluded).description)" />
      </TextHider2>
      
      <div class="bg-tiger-100 flex items-center text-shadow-none p-4 rounded-lg text-tiger-950" v-else-if="event.isOrganizing(currentUser?._id)">
        <p class="grow">{{ $t('components.Event.page.descriptionMissing') }}</p>
        <ButtonLink :to="{ name: 'app-events-slug-edit', params: { slug: event.slug }}">{{ $t('actions.edit') }}</ButtonLink>
      </div>

      <TranslationButton class="mt-2" :event="event" v-if="(event.lang !== currentUser?.langTarget) && currentUser?.isLangTranslate" />

      <ul class="mt-6 list-disc text-md font-bold pl-6 space-y-2" v-if="(event?.highlights ?? []).filter(h => h).length > 0">
        <li v-for="highlight in (event.highlights ?? []).filter(h => h)">
          {{ highlight }}
        </li>
      </ul>

      <div class="mt-6 space-y-4">
        <EventPageAttendees :event="event" :is-pending="isPending" />

        <NuxtLink :to="{ query: { ...(route?.query ?? {}), organization: organization._id }}" class="block mt-4 relative bg-dark-950 rounded-2xl text-shadow overflow-hidden" v-if="organization">
          <!-- <video class="absolute w-full h-full object-cover opacity-30" autoplay loop muted playsinline>
            <source :src="'https://vibes-production.s3.eu-west-3.amazonaws.com/static/2022395-hd_1920_1080_30fps.mp4'" type="video/mp4" />
          </video> -->
          <img :src="organization.banner?.getSize('m')" class="absolute w-full h-full object-cover opacity-50" v-if="organization.banner" />

          <div class="flex space-x-3 items-center p-3 relative z-10">
            <div class="shrink-0 size-12 bg-white rounded-xl overflow-hidden">
              <img :src="organization.logo?.getSize('s')" class="size-12" v-if="organization.logo" />
            </div>
            
            <div class="grow text-shadow-sm">
              <div class="flex items-center space-x-1">
                <p class="font-bold line-clamp-1 break-all">{{ organization.name }} </p>
                <IconBase name="cute/verified-gradient" :width="18" class="shrink-0" v-if="organization.isCertified" />
              </div>

              <div class="flex items-center space-x-2">
                <ButtonLink @click.prevent="onFollow" :is-loading="isLoading" size="sm" v-if="!followingOrganizations.includes(organization._id)">
                  {{ $t('actions.follow') }}
                </ButtonLink>

                <p class="font-medium text-sm leading-tight text-white/80" v-if="organization.followerCount && organization.followerCount > 0">
                  <span>{{ $t('common.followerCount', { count: toK(organization.followerCount ?? 0) }) }}</span>
                </p>
              </div>
              

              <!-- <div class="line-clamp-1 break-all text-sm font-medium">{{ stripTags(organization.description) }}</div> -->
            </div>

            <div class="shrink-0">
              <IconBase name="cute/angle-right" />
            </div>
          </div>
        </NuxtLink>

        <NuxtLink :to="{ name: 'app-groups-slug', params: { slug: group.slug }}" class="flex items-center space-x-3 pt-4 border-t border-white/10" v-if="group && event.group">
          <ConversationIcon :group="group" v-if="group" />

          <div class="grow">
            <p class="font-bold line-clamp-1 break-all text-md">
              <span v-if="group">{{ group.name }}</span>
              <SkeletonText class="opacity-10" v-else>dolor sit amet</SkeletonText>
            </p>
            <div class="font-medium text-sm flex items-center space-x-1">
              <template v-if="group?.isMember(currentUser?._id)">
                <IconBase name="cute/check-double" class="align-middle" :width="16" />
                <p class="leading-snug line-clamp-1">{{ $t('components.Group.member') }}</p>
              </template>
              <template v-else-if="group?.isPending(currentUser?._id)">
                <IconBase name="cute/spinner" class="align-middle spin" :width="16" />
                <p class="leading-snug line-clamp-1">{{ $t('components.Group.memberPending') }}</p>
              </template>
              <template v-else>
                <p class="leading-snug line-clamp-1 break-all">{{ stripTags(group?.description) }}</p>
              </template>
            </div>
          </div>

          <div class="shrink-0">
            <IconBase name="cute/angle-right" />
          </div>
        </NuxtLink>
      
        <div class="pt-4 border-t border-white/10 space-y-3">
          <div class="flex items-center space-x-3">
            <div class="grow">
              <p class="font-bold text-md">
                {{
                  event.date ? dayjs(event.date).format($t('moment.formatMedium')) : $t('moment.empty')
                }} 
              </p>
              <div class="font-medium text-sm leading-snug mt-1">
                <a :href="place?.urls?.maps ?? ''" class="block" target="_blank" v-if="place || !isPending">
                  <template v-if="event.type === 'online'">
                    {{ $t('components.Event.online.label') }}
                  </template>
                  <template v-else>
                    {{ place?.name ?? $t('components.Event.placeUndefined') }}{{ place?.address ? ', ' + place?.addressSimple : '' }} <IconBase name="cute/external" class="inline-block align-text-bottom" :width="16" />
                  </template>
                </a>
                <div class="opacity-10" v-else>
                  <SkeletonText class="mr-1">Lorem</SkeletonText>
                  <SkeletonText class="mr-1">Lorem ipsum</SkeletonText>
                  <SkeletonText class="mr-1">dolor sit amet</SkeletonText>
                  <SkeletonText class="mr-1">consectur</SkeletonText>
                  <SkeletonText>consectur</SkeletonText>
                </div>
              </div>
            </div>
            
            <EventCalendarButton :event="event" class="shrink-0" v-if="event.date">
              <ButtonBase style-type="round" color="white">
                <IconBase name="cute/calendar-plus" :width="21" />
              </ButtonBase>
            </EventCalendarButton>
          </div>
        </div>

        <div class="space-y-2">
          <div class="bg-urchin-100 text-urchin-950 text-shadow-none font-medium rounded-md text-left p-3 space-x-2 leading-tight flex" v-if="!event.date">
            <ImageEmoji name="Spiral+Calendar" class="size-6 shrink-0 -ml-0.5" />

            <div class="grow" v-if="event.isOrganizing(currentUser?._id)">
              <b>{{ $t('moment.empty') }}</b> <ButtonLink :to="{ name: 'app-events-slug-edit', params: { slug: event.slug } }">{{ $t('actions.edit') }}</ButtonLink>
            </div>
            <div class="grow" v-else>
              <b>{{ $t('components.Event.page.dateEmpty') }}</b> 
            </div>
          </div>

          <div class="bg-white/10 text-shadow-none font-medium rounded-md p-3 space-x-2 flex items-start" v-if="event.timing">
            <ImageEmoji name="Alarm+Clock" size="sm" class="size-6 shrink-0 -ml-0.5" />
            <div class="grow">{{ event.translated(currentUser, langExcluded).timing }}</div>
          </div>

          <div class="space-y-1" v-if="(event.links ?? []).length > 0">
            <NuxtLink :href="link.url" target="_blank" class="py-2.5 flex items-center space-x-2" :class="[ i > 0 ? 'border-t border-white/10' : '' ]" v-for="(link, i) in event.links">
              <div class="grow">
                <p class="font-bold line-clamp-1 break-all text-base leading-tight" v-if="link.title">{{ link.title }}</p>
                <p class="text-sm mt-0.5 opacity-80 line-clamp-1 break-all leading-tight">{{ beautifyLink(link.url, { path: true }) }}</p>
              </div>

              <IconBase name="cute/external" />
            </NuxtLink>
          </div>

          <div class="bg-tiger-100/90 text-shadow-none text-tiger-950 rounded-md font-medium overflow-hidden p-3 flex items-start space-x-2" v-if="event.warning">
            <ImageEmoji name="Warning" size="sm" class="size-6 shrink-0 -ml-0.5" />
            <div class="grow">{{ event.translated(currentUser, langExcluded).warning }}</div>
          </div>
        </div>

        <div class="pt-4 border-t border-white/10 space-y-3" v-if="(!group || group.isMember(currentUser?._id)) && conversation && event.canJoin(currentUser)">
          <p class="font-medium" v-if="!conversation.isParticipant(currentUser?._id)">{{ $t('components.Event.page.conversation') }}</p>
          
          <ButtonBase color="white" size="sm" is-full @click="onConversationJoin" :is-loading="isLoading">
            <IconBase name="cute/message" class="mr-1.5" />
            {{ conversation.isParticipant(currentUser?._id) ? 'Voir la conversation' : $t('actions.joinConversation') }}
          </ButtonBase>
        </div>

        <NuxtLink :to="{ name: 'app-hangout-meetups', query: { categories: event.categoriesMap.join(',') } }" class="pt-4 border-t border-white/10 flex items-center space-x-2" v-if="event.categoriesMap.length > 0">
          <div class="grow">
            <div class="font-medium text-xs leading-tight">
              {{ $t('components.Event.categories.title') }}
            </div>
            <div class="font-bold leading-tight">
              {{ event.categoriesMap.map(c => $t(`components.Event.categories.${c}.title`)).join(', ') }}
            </div>
          </div>

          <div class="shrink-0 flex items-center space-x-0.5">
            <ImageEmoji v-for="category in event.categoriesMap" class="size-8" size="sm" :name="EventCategories[category]?.image ?? ''" />
          </div>
        </NuxtLink>
      </div>
    </div>

    <PlaceInfo class="mt-4 -mx-4 pb-56 rounded-t-3xl overflow-hidden bg-light-100 dark:bg-dark-900" :place="place" :is-map="route.name !== 'app-map'" v-if="place" />
    <div class="h-56" v-else />
  </div>
</template>

<script lang="ts" setup>
import { Event, EventCategories, Group } from '@/classes'
import UserStore from '@/store/user'
import GroupStore from '@/store/groups'
import PlacesStore from '@/store/places'
import OrganizationStore from '@/store/organization'
import ConversationStore from '@/store/conversations'
import dayjs from 'dayjs'

const props = withDefaults(defineProps<{
  event: Event
  isPending?: boolean
  isTitle?: boolean
}>(), {
  isTitle: true
})

const conversationStore = ConversationStore()
const conversation = computed(() => conversationStore.getItem(props.event.conversations?.[0]))

const placesStore = PlacesStore()
const groupsStore = GroupStore()
const organizationStore = OrganizationStore()
const userStore = UserStore()
const { currentUser, followingOrganizations, langExcluded } = storeToRefs(userStore)

const route = useRoute()

const place = computed(() => placesStore.getItem(props.event.place ?? ''))
const group = computed(() => props.event.group ? groupsStore.getItem(props.event.group) : null)
const organization = computed(() => props.event.organization ? organizationStore.getItem(props.event.organization) : null)

const image = computed(() => props.event?.color && (props.event?.color as any) !== 'default' ? props.event?.color : props.event?.image?.palette)
const { palette } = getEventPalette(image)

const isLoading = ref(false)
const router = useRouter()

const onFollow = async () => {
  if (!currentUser.value || !organization.value) return

  isLoading.value = true
  
  await organizationStore.follow(organization.value._id, followingOrganizations.value.includes(organization.value._id) ? 'unfollow' : 'follow')

  isLoading.value = false
}

const onConversationJoin = async () => {
  if (!conversation.value) return

  isLoading.value = true

  try {
    await conversationStore.join(conversation.value._id)
    router.push({ name: 'app-conversation-id', params: { id: conversation.value._id } })
  } catch (error) {
    console.error(error)
  }

  isLoading.value = false
}

</script>